// FICHIER : frontend/src/pages/Contenu.jsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import NavLogo from '../components/NavLogo';

// ── URL du backend ──
const API = 'https://ark-recruitment-backend.onrender.com/api';

// ══════════════════════════════════════════
// ── Icônes ──
// ══════════════════════════════════════════
const IconCheck = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IconCopy  = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>);
const IconArrow = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>);

// ══════════════════════════════════════════
// ── Types de contenu disponibles ──
// ══════════════════════════════════════════
const TYPES_CONTENU = [
  { key: 'linkedin', label: 'Post LinkedIn',      desc: 'Post de recrutement optimisé pour LinkedIn' },
  { key: 'whatsapp', label: 'Message WhatsApp',   desc: 'Message court et direct pour WhatsApp' },
  { key: 'annonce',  label: "Annonce d'emploi",   desc: 'Annonce structurée prête à publier' },
  { key: 'script',   label: 'Script vidéo',       desc: "Script de présentation employeur 60-90 secondes" },
];

// ══════════════════════════════════════════
// ── Composant bloc résultat copiable ──
// ══════════════════════════════════════════
function ResultatContenu({ contenu, conseil }) {
  const [copie, setCopie] = useState(false);

  const copier = () => {
    navigator.clipboard.writeText(contenu).then(() => {
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    });
  };

  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--accent)', borderRadius: 14, overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg2)' }}>
        <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--accent2)', letterSpacing: 1, textTransform: 'uppercase' }}>
          Contenu généré
        </div>
        <button
          onClick={copier}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: copie ? 'rgba(22,163,74,0.08)' : 'var(--accent)', color: copie ? 'var(--success)' : '#fff', border: copie ? '1px solid var(--success)' : 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif', transition: 'all 0.2s' }}
        >
          {copie ? <><IconCheck/> Copié !</> : <><IconCopy/> Copier</>}
        </button>
      </div>

      {/* Contenu */}
      <pre style={{ padding: '20px 18px', fontSize: 14, color: 'var(--text)', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'DM Sans,sans-serif' }}>
        {contenu}
      </pre>

      {/* Conseil */}
      {conseil && (
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', background: 'rgba(74,150,150,0.06)' }}>
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 10, color: 'var(--accent2)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Conseil</div>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, lineHeight: 1.6 }}>{conseil}</p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// ── Composant principal Contenu ──
// ══════════════════════════════════════════
export default function Contenu() {
  const navigate = useNavigate();

  // ── Détection mobile ──
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── Type de contenu sélectionné ──
  const [typeActif, setTypeActif] = useState('linkedin');

  // ── Formulaire contexte marque ──
  const [form, setForm] = useState({
    nomEntreprise: '',
    secteur:       '',
    pitch:         '',
    evp:           '',
    poste:         '',
  });
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  // ── États génération ──
  const [loading,  setLoading]  = useState(false);
  const [resultat, setResultat] = useState(null);
  const [error,    setError]    = useState('');

  // ── Appel backend DeepSeek ──
  const generer = async () => {
    setError('');
    if (!form.nomEntreprise.trim()) {
      setError("Le nom de l'entreprise est obligatoire.");
      return;
    }
    setLoading(true);
    setResultat(null);
    try {
      const companyId = localStorage.getItem('company_id') || '';
      const res = await axios.post(`${API}/contenu/generer`, {
        company_id: companyId,
        type: typeActif,
        ...form,
      });
      setResultat(res.data);
    } catch (e) {
      setError('Erreur de génération. Vérifiez que le backend est en ligne.');
    } finally {
      setLoading(false);
    }
  };

  // ── Style input ──
  const inputStyle = { width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: 'var(--text)', outline: 'none', fontFamily: 'DM Sans,sans-serif' };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'DM Sans,sans-serif' }}>

      {/* ── NAVIGATION ── */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 20px' : '0 48px', height: 64, borderBottom: '1px solid var(--border)', background: 'var(--nav-bg)' }}>
        <NavLogo/>
        {!isMobile && (
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--muted)', letterSpacing: 1 }}>
            GÉNÉRER DU <span style={{ color: 'var(--accent2)' }}>CONTENU</span>
          </div>
        )}
        <span onClick={() => navigate('/dashboard')} style={{ color: 'var(--muted)', fontSize: 13, cursor: 'pointer', fontFamily: 'DM Mono,monospace', letterSpacing: 0.5 }}>
          ← Dashboard
        </span>
      </nav>

      {/* ── CONTENU ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '24px 16px env(safe-area-inset-bottom, 24px)' : '40px 32px' }}>

        {/* En-tête */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--accent2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Contenu RH</div>
          <h1 style={{ fontFamily: 'Syne,sans-serif', fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-0.8px', marginBottom: 6 }}>
            Générer du contenu de recrutement
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
            Posts LinkedIn, messages WhatsApp, annonces, scripts vidéo — générés par DeepSeek en quelques secondes.
          </p>
        </div>

        {/* ── Sélecteur de type ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10, marginBottom: 32 }}>
          {TYPES_CONTENU.map(t => (
            <button
              key={t.key}
              onClick={() => { setTypeActif(t.key); setResultat(null); }}
              style={{ background: typeActif === t.key ? 'var(--accent-08)' : 'var(--bg3)', border: `1px solid ${typeActif === t.key ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 12, padding: '16px 14px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
            >
              <div style={{ fontFamily: 'Syne,sans-serif', fontSize: 13, fontWeight: 700, color: typeActif === t.key ? 'var(--accent)' : 'var(--text)', marginBottom: 4 }}>{t.label}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>{t.desc}</div>
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 0 : 32 }}>

          {/* ── COLONNE GAUCHE — Formulaire ── */}
          <div>
            <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
              Contexte de votre entreprise
            </div>

            {/* Nom entreprise */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>Nom de l'entreprise *</label>
              <input type="text" value={form.nomEntreprise} onChange={set('nomEntreprise')} placeholder="Ex : Ark Intelligence" style={inputStyle}/>
            </div>

            {/* Secteur */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>Secteur d'activité</label>
              <input type="text" value={form.secteur} onChange={set('secteur')} placeholder="Ex : Intelligence artificielle, Finance..." style={inputStyle}/>
            </div>

            {/* Pitch */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>Pitch entreprise</label>
              <textarea value={form.pitch} onChange={set('pitch')} placeholder="Décrivez votre entreprise en 2-3 phrases..." rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}/>
            </div>

            {/* EVP */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>Proposition de valeur employeur</label>
              <textarea value={form.evp} onChange={set('evp')} placeholder="Pourquoi rejoindre votre entreprise ?" rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}/>
            </div>

            {/* Poste (optionnel) */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>Poste à pourvoir (optionnel)</label>
              <input type="text" value={form.poste} onChange={set('poste')} placeholder="Ex : Développeur React Senior" style={inputStyle}/>
            </div>

            {/* Erreur */}
            {error && (
              <div style={{ background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.12)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--error)', marginBottom: 16 }}>
                {error}
              </div>
            )}

            {/* Bouton générer */}
            <button
              onClick={generer}
              disabled={loading}
              style={{ width: '100%', background: loading ? 'var(--border)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 20px', fontSize: 15, fontWeight: 600, fontFamily: 'DM Sans,sans-serif', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              {loading
                ? 'Génération en cours…'
                : (<>Générer — {TYPES_CONTENU.find(t => t.key === typeActif)?.label} <IconArrow/></>)
              }
            </button>
          </div>

          {/* ── COLONNE DROITE — Résultat ── */}
          <div style={{ marginTop: isMobile ? 32 : 0 }}>
            <div style={{ position: isMobile ? 'relative' : 'sticky', top: isMobile ? 0 : 80 }}>

              {/* Placeholder vide */}
              {!resultat && !loading && (
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 14, padding: '48px 20px', textAlign: 'center', color: 'var(--muted)' }}>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--border)' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 12, letterSpacing: 1 }}>
                    Remplissez le contexte<br/>et cliquez sur Générer
                  </div>
                </div>
              )}

              {/* Chargement */}
              {loading && (
                <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 14, padding: '48px 20px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'DM Mono,monospace', fontSize: 12, letterSpacing: 1 }}>
                  DeepSeek génère votre contenu…
                </div>
              )}

              {/* Résultat */}
              {resultat && !loading && (
                <>
                  <ResultatContenu contenu={resultat.contenu} conseil={resultat.conseil}/>

                  {/* Bouton regénérer */}
                  <button
                    onClick={generer}
                    style={{ width: '100%', marginTop: 12, background: 'none', border: '1px solid var(--border)', borderRadius: 10, padding: '11px 20px', fontSize: 13, color: 'var(--muted)', cursor: 'pointer', fontFamily: 'DM Sans,sans-serif' }}
                  >
                    Regénérer
                  </button>
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}